import { getImageFileFromDataUrl } from "../lib/image";
import {
  SMILE_SIMULATION_API_URL,
  SMILE_SIMULATION_ASPECT_RATIO,
} from "../model/constants";
import type { SmileSimulationPromptVersion } from "../model/types";

type CallSmileSimulationAPIInput = {
  authToken?: string;
  chartNumber?: string;
  eventMode?: "experience";
  imageDataUrl: string;
  patientName?: string;
  promptVersion: SmileSimulationPromptVersion;
};

type CallSmileSimulationAPIResult = {
  image: Blob;
  simulationId: string | null;
};

type SmileSimulationAPIJsonResponse = {
  imageBase64?: string;
  imageMimeType?: string;
  simulationId?: string | null;
};

const getBlobFromBase64 = (base64: string, mimeType: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
};

export const callSmileSimulationAPI = async ({
  authToken,
  chartNumber,
  eventMode,
  imageDataUrl,
  patientName,
  promptVersion,
}: CallSmileSimulationAPIInput): Promise<CallSmileSimulationAPIResult> => {
  const formData = new FormData();
  const imageFile = getImageFileFromDataUrl(imageDataUrl, "original.png");

  formData.append("image", imageFile, imageFile.name);
  formData.append("aspectRatio", SMILE_SIMULATION_ASPECT_RATIO);
  formData.append("promptVersion", promptVersion);

  if (patientName) {
    formData.append("patientName", patientName);
  }

  if (chartNumber) {
    formData.append("chartNumber", chartNumber);
  }

  if (eventMode) {
    formData.append("eventMode", eventMode);
  }

  const headers = authToken
    ? {
        Authorization: `Bearer ${authToken}`,
      }
    : undefined;

  const response = await fetch(SMILE_SIMULATION_API_URL, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} - ${errorText}`);
  }

  const contentType = response.headers.get("content-type");

  if (contentType?.startsWith("application/json")) {
    const responseJson =
      (await response.json()) as SmileSimulationAPIJsonResponse;

    if (!responseJson.imageBase64 || !responseJson.imageMimeType) {
      throw new Error("시뮬레이션 결과 이미지를 받지 못했어요.");
    }

    return {
      image: getBlobFromBase64(
        responseJson.imageBase64,
        responseJson.imageMimeType,
      ),
      simulationId:
        responseJson.simulationId ??
        response.headers.get("X-Smile-Simulation-Id"),
    };
  }

  if (contentType && !contentType.startsWith("image/")) {
    const errorText = await response.text();
    throw new Error(`API Error: unexpected response type - ${errorText}`);
  }

  const resultImage = await response.blob();

  if (!resultImage.size) {
    throw new Error("시뮬레이션 결과 이미지를 받지 못했어요.");
  }

  return {
    image: resultImage,
    simulationId: response.headers.get("X-Smile-Simulation-Id"),
  };
};
